/**
 * App — the editor shell.
 *
 * Layout:
 *   ┌─────────────────────────────────────────────┐
 *   │ Toolbar                                     │
 *   ├─────────┬──────────────────────┬────────────┤
 *   │ Palette │ Canvas (A4)          │ Properties │
 *   │         │                      │            │
 *   │         │                      │            │
 *   └─────────┴──────────────────────┴────────────┘
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { computePinKeys } from './layout/pinKey';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  SelectionMode,
  addEdge,
  applyNodeChanges,
  applyEdgeChanges,
  type NodeChange,
  type EdgeChange,
  Connection,
  useReactFlow,
  Panel
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import { useEditorStore, PAPER_DIMENSIONS, type SldNode, type SldEdge } from './store/editor';
import { samplePresets } from './store/presets';
import { autoLayout, makeTitleBlockNode } from './layout/autoLayout';
import { SYMBOLS, renderPanelString } from './symbols/library';
import { INVERTER_CATALOG, inverterFromCatalog } from './catalog/inverters';
import { BATTERY_CATALOG, batteryFromCatalog } from './catalog/batteries';
import { typesCompatible, preferredWireStyle } from './types/symbol';
import { SldSymbolNode } from './components/SldSymbolNode';
import { SldWire } from './components/SldWire';
import { SymbolPalette } from './components/SymbolPalette';
import { PropertiesPanel } from './components/PropertiesPanel';
import { PageFrameNode } from './components/PageFrameNode';
import { exportPdf } from './export/exportPdf';
import { reportSldClientError } from './api/reportClientError';
import { Menu } from './components/Menu';
import { CommandPalette, type Command } from './components/CommandPalette';
import { NewDiagramConfig } from './components/NewDiagramConfig';
import { ProUpgradeModal, isProUpgradeSuppressed } from './components/ProUpgradeModal';
import { RevisionsMenu } from './components/RevisionsMenu';
import { useAutoSaveRevision } from './hooks/useAutoSaveRevision';
import { onBrandLogosLoaded } from './catalog/brandLogos';

const nodeTypes = { sldSymbol: SldSymbolNode, pageFrame: PageFrameNode };
const edgeTypes = { sldWire: SldWire };

/** Build the special page-frame node for the current paper size. */
function pageFrameNode(paperSize: keyof typeof PAPER_DIMENSIONS): SldNode {
  const dims = PAPER_DIMENSIONS[paperSize];
  return {
    id: '__page_frame__',
    type: 'pageFrame',
    position: { x: 0, y: 0 },
    data: { w: dims.w, h: dims.h, label: dims.label } as any,
    draggable: false,
    selectable: false,
    focusable: false,
    deletable: false,
    zIndex: -1
  };
}

function Canvas() {
  // SINGLE SOURCE OF TRUTH: nodes + edges live in Zustand only.
  //
  // The earlier version had React Flow's useNodesState + useEdgesState
  // hooks holding LOCAL state, with two useEffects bidirectionally
  // syncing local <-> Zustand. That dual-state pattern is famously
  // fragile: any time React Flow's StoreUpdater receives a new `nodes`
  // prop ref, it pushes into its internal store, which re-renders
  // components using useStore, which can re-render Canvas, which can
  // change the `nodes` prop again — a loop the React error called
  // out as "Maximum update depth exceeded".
  //
  // Fix: keep nodes/edges in Zustand only. React Flow gets them as
  // props and emits change events; we apply those changes directly
  // back to the Zustand store. There is no second copy to drift.
  const topology    = useEditorStore((s) => s.topology);
  const nodes       = useEditorStore((s) => s.nodes);
  const edges       = useEditorStore((s) => s.edges);
  const setNodes    = useEditorStore((s) => s.setNodes);
  const setEdges    = useEditorStore((s) => s.setEdges);
  const paperSize   = useEditorStore((s) => s.paperSize);
  const pushHistory = useEditorStore((s) => s.pushHistory);
  const undo        = useEditorStore((s) => s.undo);
  const redo        = useEditorStore((s) => s.redo);

  // Selection lives in the store so equipment-summary "Edit" buttons in
  // PropertiesPanel can programmatically focus a symbol on the canvas.
  const selectedNodeId    = useEditorStore((s) => s.selectedNodeId);
  const setSelectedNodeIdLocal = useEditorStore((s) => s.setSelectedNodeId);

  // In-session clipboard for Cmd/Ctrl+C / V. Holds copies of selected
  // user nodes + their fully-internal wires. Not persisted — paste
  // survives within the page session only (matches normal app clipboard
  // expectations and avoids leaking selection state across sessions).
  const clipboardRef = useRef<{ nodes: SldNode[]; edges: SldEdge[] } | null>(null);
  const { screenToFlowPosition, fitView } = useReactFlow();

  // React Flow's change handlers — we apply each batch of changes to
  // the store via applyNodeChanges / applyEdgeChanges (the same
  // helpers React Flow uses internally). The store's set replaces the
  // array reference, components re-render once, end of story. No
  // bidirectional sync, no loop.
  const onNodesChange = useCallback((changes: NodeChange<SldNode>[]) => {
    setNodes((nds) => applyNodeChanges(changes, nds) as SldNode[]);
    // A finished drag (position change with dragging=false) is a
    // deliberate placement - pin it so later re-layouts keep it.
    const pin = useEditorStore.getState().pinNodePosition;
    for (const c of changes) {
      if (c.type === 'position' && c.dragging === false) pin(c.id);
    }
  }, [setNodes]);
  const onEdgesChange = useCallback((changes: EdgeChange<SldEdge>[]) => {
    setEdges((eds) => applyEdgeChanges(changes, eds) as SldEdge[]);
  }, [setEdges]);

  // Re-layout whenever topology changes (or on initial mount).
  // Always inject the page frame at the start so it renders behind everything.
  //
  // SKIP-IF-RESTORED guard: when the store rehydrates from localStorage
  // (standalone persistence), nodes/edges are already populated with the
  // user's last-saved layout. Re-running autoLayout would clobber any
  // manual position adjustments, label edits, or extra symbols they
  // dragged in. We only run autoLayout when nodes is essentially empty
  // — meaning a fresh start or a preset switch (loadTopology clears
  // nodes explicitly so this guard releases on preset changes).
  useEffect(() => {
    // Topology-derived nodes only — annotations (EXISTING wrapper boxes)
    // and chrome (page frame) shouldn't count as "the user has work to
    // preserve". applyTopologyAndRelayout deliberately keeps the wrapper
    // annotations and clears the rest, so this effect needs to re-run
    // autoLayout when no topology-derived nodes remain.
    const topoNodes = nodes.filter(
      (n) => n.id !== '__page_frame__' && !n.id.startsWith('existing-boundary-')
    );
    if (topoNodes.length > 0) {
      // Restored from storage — keep the existing layout, just make sure
      // the page frame matches the current paper size.
      setNodes((prev) => {
        const withoutFrame = prev.filter((n) => n.id !== '__page_frame__');
        return [pageFrameNode(paperSize), ...withoutFrame];
      });
      const t = setTimeout(() => fitView({ padding: 0.15, duration: 400 }), 50);
      return () => clearTimeout(t);
    }

    const laid = autoLayout(topology);
    // Re-apply any hand-placed positions the user pinned before this
    // regen (see store.pinNodePosition / layout/pinKey.ts). Symbols
    // that no longer exist simply have no match and are ignored.
    const pins = useEditorStore.getState().pinnedPositions;
    const laidNodes = (() => {
      if (!pins || Object.keys(pins).length === 0) return laid.nodes;
      const keys = computePinKeys(laid.nodes);
      return laid.nodes.map((n) => {
        const k = keys.get(n.id);
        const p = k ? pins[k] : undefined;
        return p ? { ...n, position: { x: p.x, y: p.y } } : n;
      });
    })();
    // Splice the auto-laid nodes BETWEEN the page frame and any preserved
    // annotation wrappers, so wrappers stay behind the freshly-laid symbols.
    const annotations = nodes.filter((n) => n.id.startsWith('existing-boundary-'));
    const allNodes = [pageFrameNode(paperSize), ...annotations, ...laidNodes];
    setNodes(allNodes);
    setEdges(laid.edges);
    setSelectedNodeIdLocal(null);
    const t = setTimeout(() => fitView({ padding: 0.15, duration: 400 }), 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topology]);

  // Swap page frame when paper size changes (without re-running layout)
  useEffect(() => {
    setNodes((prev) => {
      const withoutFrame = prev.filter((n) => n.id !== '__page_frame__');
      return [pageFrameNode(paperSize), ...withoutFrame];
    });
    const t = setTimeout(() => fitView({ padding: 0.15, duration: 400 }), 50);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paperSize]);

  // Live-refresh the title block whenever ANY topology field changes —
  // project info edits, equipment swaps, installer details, etc — so the
  // title block stays in sync without re-running the full autoLayout
  // (which would clobber user position adjustments). We preserve the
  // user's manual position of the title block if they've moved it.
  useEffect(() => {
    setNodes((prev) => {
      const fresh = makeTitleBlockNode(topology);
      return prev.map((n) => {
        if (n.id !== '__title_block__') return n;
        // Keep user's position override; only refresh the rendered content.
        return { ...fresh, position: n.position };
      });
    });
  }, [topology, setNodes]);

  // When the brand-logo manifest finishes loading from the backend,
  // re-render the title block so the BOM tables pick up real product
  // logos in place of the initials-fallback badges. Without this the
  // first paint shows initials (manifest still in flight) and the
  // user has to nudge the topology to refresh.
  useEffect(() => {
    return onBrandLogosLoaded(() => {
      setNodes((prev) => {
        const fresh = makeTitleBlockNode(useEditorStore.getState().topology);
        return prev.map((n) =>
          n.id === '__title_block__' ? { ...fresh, position: n.position } : n
        );
      });
    });
  }, [setNodes]);

  const onConnect = useCallback(
    (conn: Connection) => {
      // Type-check the connection
      const srcNode = nodes.find((n) => n.id === conn.source);
      const tgtNode = nodes.find((n) => n.id === conn.target);
      if (srcNode && tgtNode && conn.sourceHandle && conn.targetHandle) {
        const srcSym = SYMBOLS[srcNode.data.symbolId];
        const tgtSym = SYMBOLS[tgtNode.data.symbolId];
        const srcTerm = srcSym?.terminals[conn.sourceHandle];
        const tgtTerm = tgtSym?.terminals[conn.targetHandle];
        if (srcTerm && tgtTerm && !typesCompatible(srcTerm.type, tgtTerm.type)) {
          // Async fire-and-forget — the user just needs to acknowledge.
          // We bail out of the connection regardless of when they
          // dismiss, which is why we don't await the promise.
          void useEditorStore.getState().askConfirm({
            title: 'Incompatible terminals',
            message:
              `Can't connect a ${srcTerm.type.toUpperCase()} terminal to a ${tgtTerm.type.toUpperCase()} terminal — ` +
              `they carry different signal types. Try connecting matching terminals (DC↔DC, AC↔AC, earth↔earth).`,
            singleButton: true,
            tone: 'warning',
          });
          return;
        }
        // Snapshot BEFORE the new edge is added so undo restores the
        // pre-connection state.
        pushHistory();
        // Pick the more SPECIFIC of the two terminal types so the
        // wire colour is the same regardless of which end the user
        // dragged from. Without this, a drag panel→inverter gives
        // a red wire (source is dc+) but drag inverter→panel gives
        // a black wire (source is unpolarised dc) — same wire,
        // different colour, depending on direction. preferredWireStyle
        // resolves to the polarised end whenever it exists.
        const style =
          srcTerm && tgtTerm
            ? preferredWireStyle(srcTerm.type, tgtTerm.type)
            : srcTerm?.type ?? tgtTerm?.type ?? 'any';
        setEdges((eds) =>
          addEdge(
            { ...conn, type: 'sldWire', data: { style } } as SldEdge,
            eds
          )
        );
        return;
      }
      pushHistory();
      setEdges((eds) => addEdge({ ...conn, type: 'sldWire' } as SldEdge, eds));
    },
    [nodes, setEdges, pushHistory]
  );

  // History commit hooks for React Flow's lifecycle events.
  // We snapshot ONCE per drag (on dragStart) so undo restores the position
  // the symbol was at before you started dragging it — not where it was
  // mid-drag tick. Same idea for selection-driven drags of multi-select groups.
  const onNodeDragStart = useCallback(() => { pushHistory(); }, [pushHistory]);
  // For deletes we snapshot BEFORE the change applies via onBeforeDelete.
  const onBeforeDelete = useCallback(() => {
    pushHistory();
    return Promise.resolve(true);
  }, [pushHistory]);

  const isValidConnection = useCallback(
    (conn: Connection | SldEdge) => {
      const srcNode = nodes.find((n) => n.id === conn.source);
      const tgtNode = nodes.find((n) => n.id === conn.target);
      if (!srcNode || !tgtNode || !conn.sourceHandle || !conn.targetHandle) return true;
      const srcTerm = SYMBOLS[srcNode.data.symbolId]?.terminals[conn.sourceHandle];
      const tgtTerm = SYMBOLS[tgtNode.data.symbolId]?.terminals[conn.targetHandle];
      if (!srcTerm || !tgtTerm) return true;
      return typesCompatible(srcTerm.type, tgtTerm.type);
    },
    [nodes]
  );

  // Drag-drop from palette
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const symbolId = event.dataTransfer.getData('application/reactflow-sld-symbol');
      if (!symbolId || !SYMBOLS[symbolId]) return;
      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY
      });

      // Default node — used by every symbol except panelString.
      let newNode: SldNode = {
        id: `${symbolId}-${Date.now()}`,
        type: 'sldSymbol',
        position,
        data: { symbolId, rotation: 0, label: SYMBOLS[symbolId].label }
      };

      // For "managed" symbols (inverter, hybridInverter, battery) we
      // also register a corresponding topology entry on drop. Without
      // this the InverterEditor / BatteryEditor can't find the entity
      // and the right panel only shows the generic rotate-and-delete
      // controls — the user's reasonable expectation when dragging a
      // SH10RT onto the canvas is that they can immediately set its
      // model, MPPTs and strings, which requires a topology entry.
      if (symbolId === 'inverter' || symbolId === 'hybridInverter') {
        const isHybrid = symbolId === 'hybridInverter';
        // Pick a sensible default model: a string SG10RT for the
        // plain inverter, an SH10RT (hybrid) for the hybrid icon.
        const defaultEntry =
          INVERTER_CATALOG.find((e) =>
            isHybrid ? e.model === 'SH10RT' : e.model === 'SG10RT'
          ) ??
          INVERTER_CATALOG.find((e) => (isHybrid ? e.type === 'hybrid' : e.type === 'string')) ??
          INVERTER_CATALOG[0];

        // Allocate a unique inv id by walking up from the current
        // count. Avoids collisions with wizard-generated invN ids.
        const existingIds = new Set(
          useEditorStore.getState().topology.inverters.map((i) => i.id)
        );
        let invId = `inv${existingIds.size + 1}`;
        let n = existingIds.size + 2;
        while (existingIds.has(invId)) { invId = `inv${n}`; n++; }

        // inverterFromCatalog() pre-populates 2 MPPTs × 1 string × 10
        // panels — sensible for the wizard (which then auto-lays out
        // matching panel-string blocks on the canvas) but confusing
        // for hand-dropped inverters: the editor would claim strings
        // the canvas doesn't show. Start with EMPTY mppts so the
        // editor honestly says "no strings declared", and the user
        // either adds them via "+ Add MPPT" or drops free Panel
        // String blocks and wires them up.
        const newInverter = {
          ...inverterFromCatalog(defaultEntry, { id: invId }),
          mppts: [],
        };

        // Push topology + the dropped node in one history entry, but
        // keep the node where the user dropped it (don't re-layout).
        useEditorStore.getState().updateTopology((t) => ({
          ...t,
          inverters: [...t.inverters, newInverter],
        }));
        newNode = {
          id: `${symbolId}-${Date.now()}`,
          type: 'sldSymbol',
          position,
          data: {
            symbolId,
            rotation: 0,
            label: defaultEntry.model,
            sublabel: `${(defaultEntry.capacity / 1000).toFixed(1)} kW · ${defaultEntry.phases}φ`,
            topologyRef: { kind: 'inverter', id: invId },
          },
        };
        pushHistory();
        setNodes((nds) => nds.concat(newNode));
        return;
      }

      if (symbolId === 'battery') {
        // Pick the first AC-coupled entry as a sensible default
        // (Powerwall 3 is a good fallback). DC-coupled requires a
        // hybrid inverter to attach to, which dragging-from-palette
        // can't reliably establish, so we always pick AC.
        const defaultEntry =
          BATTERY_CATALOG.find((e) => e.coupling === 'ac') ?? BATTERY_CATALOG[0];

        const existingIds = new Set(
          useEditorStore.getState().topology.batteries.map((b) => b.id)
        );
        let batId = `bat${existingIds.size + 1}`;
        let n = existingIds.size + 2;
        while (existingIds.has(batId)) { batId = `bat${n}`; n++; }

        const newBattery = batteryFromCatalog(defaultEntry, { id: batId });

        useEditorStore.getState().updateTopology((t) => ({
          ...t,
          batteries: [...t.batteries, newBattery],
        }));
        newNode = {
          id: `battery-${Date.now()}`,
          type: 'sldSymbol',
          position,
          data: {
            symbolId: 'battery',
            rotation: 0,
            label: defaultEntry.model,
            sublabel: `${defaultEntry.capacity} kWh · ${defaultEntry.coupling.toUpperCase()}-coupled`,
            topologyRef: { kind: 'battery', id: batId },
          },
        };
        pushHistory();
        setNodes((nds) => nds.concat(newNode));
        return;
      }

      // panelString is a composite — build the SVG content + variable
      // box dimensions from a default config (10 panels × 1 string).
      // Stored as `freeStringConfig` so click-to-edit can re-render
      // the block when the user changes panels / parallels / model.
      if (symbolId === 'panelString') {
        const t = useEditorStore.getState().topology;
        const panelTypes = (t.panelTypes && t.panelTypes.length > 0)
          ? t.panelTypes
          : (t.panels ? [{ ...t.panels, id: 'default' }] : []);
        const panel = panelTypes[0];
        const config = { panels: 10, parallels: 1, panelTypeId: panel?.id };
        // Label format matches wizard-generated lanes: just the
        // panel model (autoLayout uses "MPPT N · Model" — for free
        // blocks we drop the "MPPT N" half since there's no MPPT
        // context). The "× N per string" badge is drawn internally
        // by renderPanelString — including it in the label too
        // would double-up the count line.
        const labelLine = panel ? panel.model : 'Panel string';
        const rendered = renderPanelString(config.panels, labelLine);
        // Per-instance terminal positions — pos and neg follow the
        // composite's actual edges, which depend on its width.
        const customTerminals = {
          pos: { id: 'pos', x: rendered.terminals.pos.x, y: rendered.terminals.pos.y, type: 'dc+' as const },
          neg: { id: 'neg', x: rendered.terminals.neg.x, y: rendered.terminals.neg.y, type: 'dc-' as const },
        };
        newNode = {
          id: `panelString-${Date.now()}`,
          type: 'sldSymbol',
          position,
          data: {
            symbolId: 'panelString',
            rotation: 0,
            customContent: rendered.content,
            customBox: rendered.nativeBox,
            customTerminals,
            freeStringConfig: config,
            label: '',
          },
        };
      }

      // Snapshot BEFORE adding so undo removes the new symbol cleanly.
      pushHistory();
      setNodes((nds) => nds.concat(newNode));
    },
    [screenToFlowPosition, setNodes, pushHistory]
  );

  // Cmd/Ctrl+Z / Cmd/Ctrl+Shift+Z keyboard handlers. Bound to window
  // (not the ReactFlow div) so they work even when focus is on the
  // toolbar or properties panel — except when the user is typing in
  // an <input> / <textarea> / contentEditable, which we detect and
  // skip so undo doesn't trigger inside a label edit field.
  useEffect(() => {
    const isEditableTarget = (el: EventTarget | null): boolean => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      return false;
    };
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      if (e.key !== 'z' && e.key !== 'Z') return;
      if (isEditableTarget(e.target)) return;
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [undo, redo]);

  // Copy / paste — Cmd/Ctrl+C captures the selected user nodes (skips
  // chrome: page frame + title block) plus any wires fully internal to
  // the selection. Cmd/Ctrl+V drops a deep clone with new node ids,
  // offset 30px so the duplicate is visible. Clipboard lives in a ref
  // (intentionally NOT persisted — paste survives within a session
  // only, like a normal app's clipboard).
  useEffect(() => {
    const isEditableTarget = (el: EventTarget | null): boolean => {
      if (!(el instanceof HTMLElement)) return false;
      const tag = el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
      if (el.isContentEditable) return true;
      return false;
    };
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta) return;
      if (isEditableTarget(e.target)) return;
      const k = e.key.toLowerCase();
      if (k !== 'c' && k !== 'v' && k !== 'a') return;

      // Cmd/Ctrl+A — select all user content. Excludes chrome
      // (__page_frame__, __title_block__) since those are not user
      // symbols and shouldn't be lumped in with a "select everything"
      // bulk operation. Also clears any edge selection so the
      // PropertiesPanel switches into multi-select mode cleanly.
      if (k === 'a') {
        e.preventDefault();
        setNodes((prev) =>
          prev.map((n) => {
            const isChrome =
              n.id === '__page_frame__' || n.id === '__title_block__';
            const wantSelected = !isChrome;
            return n.selected === wantSelected
              ? n
              : { ...n, selected: wantSelected };
          })
        );
        setEdges((prev) =>
          prev.map((ed) =>
            ed.selected ? { ...ed, selected: false } : ed
          )
        );
        // Clear single-selection so the right panel correctly
        // switches to MultiSelectPanel (driven by selectedNodes.length).
        setSelectedNodeIdLocal(null);
        return;
      }

      if (k === 'c') {
        const selected = nodes.filter(
          (n) => n.selected && n.id !== '__page_frame__' && n.id !== '__title_block__'
        );
        if (selected.length === 0) return;
        e.preventDefault();
        const ids = new Set(selected.map((n) => n.id));
        const internalEdges = edges.filter(
          (ed) => ids.has(ed.source) && ids.has(ed.target)
        );
        clipboardRef.current = {
          nodes: selected.map((n) => ({ ...n })),
          edges: internalEdges.map((ed) => ({ ...ed })),
        };
        return;
      }

      if (k === 'v') {
        const clip = clipboardRef.current;
        if (!clip || clip.nodes.length === 0) return;
        e.preventDefault();
        // Build old-id → new-id map for NODES
        const idMap = new Map<string, string>();
        const stamp = Date.now().toString(36);
        clip.nodes.forEach((n, i) => idMap.set(n.id, `paste-${stamp}-n${i}`));
        const offset = 30;

        // Build old-topology-id → new-topology-id map for INVERTERS
        // and BATTERIES present in the clipboard. Without this, the
        // pasted nodes would share topology ids with the originals,
        // and deleting either copy would orphan the other (BOM
        // wipes, the title block goes blank). Each pasted entity
        // gets its OWN topology entry with a fresh id.
        const t = useEditorStore.getState().topology;
        const existingInvIds = new Set(t.inverters.map((i) => i.id));
        const existingBatIds = new Set(t.batteries.map((b) => b.id));
        const invIdMap = new Map<string, string>();
        const batIdMap = new Map<string, string>();
        let invSeq = existingInvIds.size + 1;
        let batSeq = existingBatIds.size + 1;
        const allocId = (prefix: string, seqRef: { v: number }, taken: Set<string>) => {
          let id = `${prefix}${seqRef.v++}`;
          while (taken.has(id)) id = `${prefix}${seqRef.v++}`;
          taken.add(id);
          return id;
        };
        const invSeqRef = { v: invSeq };
        const batSeqRef = { v: batSeq };
        for (const n of clip.nodes) {
          const ref = n.data?.topologyRef;
          if (!ref) continue;
          if (ref.kind === 'inverter' && !invIdMap.has(ref.id)) {
            invIdMap.set(ref.id, allocId('inv', invSeqRef, existingInvIds));
          } else if (ref.kind === 'battery' && !batIdMap.has(ref.id)) {
            batIdMap.set(ref.id, allocId('bat', batSeqRef, existingBatIds));
          }
        }

        const newNodes: SldNode[] = clip.nodes.map((n) => {
          const ref = n.data?.topologyRef;
          let nextRef = ref;
          if (ref?.kind === 'inverter' && invIdMap.has(ref.id)) {
            nextRef = { ...ref, id: invIdMap.get(ref.id)! };
          } else if (ref?.kind === 'battery' && batIdMap.has(ref.id)) {
            nextRef = { ...ref, id: batIdMap.get(ref.id)! };
          }
          return {
            ...n,
            id: idMap.get(n.id)!,
            position: { x: n.position.x + offset, y: n.position.y + offset },
            selected: true,
            data: ref === nextRef ? n.data : { ...n.data, topologyRef: nextRef },
          };
        });
        const newEdges: SldEdge[] = clip.edges.map((ed, i) => ({
          ...ed,
          id: `paste-${stamp}-e${i}`,
          source: idMap.get(ed.source)!,
          target: idMap.get(ed.target)!,
          selected: false,
        }));
        pushHistory();

        // Duplicate topology entries for the freshly-mapped inverters
        // and batteries. DC battery `connectedTo` references also
        // remap so the relationship survives the paste.
        if (invIdMap.size > 0 || batIdMap.size > 0) {
          useEditorStore.getState().updateTopology((tt) => {
            const newInverters = [...tt.inverters];
            for (const [oldId, newId] of invIdMap) {
              const src = tt.inverters.find((i) => i.id === oldId);
              if (src) newInverters.push({ ...src, id: newId });
            }
            const newBatteries = [...tt.batteries];
            for (const [oldId, newId] of batIdMap) {
              const src = tt.batteries.find((b) => b.id === oldId);
              if (src) {
                const remapped = { ...src, id: newId };
                if (remapped.connectedTo && invIdMap.has(remapped.connectedTo)) {
                  remapped.connectedTo = invIdMap.get(remapped.connectedTo)!;
                }
                newBatteries.push(remapped);
              }
            }
            return { ...tt, inverters: newInverters, batteries: newBatteries };
          });
        }

        // Deselect existing, append pasted (already selected)
        setNodes((prev) => [
          ...prev.map((n) => (n.selected ? { ...n, selected: false } : n)),
          ...newNodes,
        ]);
        setEdges((prev) => [
          ...prev.map((ed) => (ed.selected ? { ...ed, selected: false } : ed)),
          ...newEdges,
        ]);
        setSelectedNodeIdLocal(newNodes[0]?.id ?? null);
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [nodes, edges, setNodes, setEdges, pushHistory, setSelectedNodeIdLocal]);

  const onNodeClick = useCallback((_e: React.MouseEvent, node: SldNode) => {
    // Page frame is a chrome element, not user content. Treat a click
    // on it as a "click on the canvas background" — clear the
    // PropertiesPanel selection AND any selected edge so the user gets
    // a clean slate. React Flow won't fire onPaneClick when a node
    // (even a non-selectable one) is the click target.
    if (node.id === '__page_frame__') {
      setSelectedNodeIdLocal(null);
      setEdges((eds) => eds.map((e) => (e.selected ? { ...e, selected: false } : e)));
      setNodes((nds) => nds.map((n) => (n.selected ? { ...n, selected: false } : n)));
      return;
    }
    // Title block — selectable so the user gets a visual target hint
    // (selection ring) and PropertiesPanel switches to the project form.
    // Falls through to the normal selection logic.
    setSelectedNodeIdLocal(node.id);
}, [setEdges, setNodes]);

  // Clicking the canvas background deselects everything: clears the
  // PropertiesPanel selection AND any selected edge (so the red ×
  // disappears from a previously-clicked wire). React Flow normally
  // does the edge-deselect itself on pane click, but the page-frame
  // node sits on top of the actual pane and intercepts some clicks
  // depending on where you click — this wrapper makes sure edges
  // also deselect when the user clearly meant "clear everything."
  const onPaneClick = useCallback(() => {
    setSelectedNodeIdLocal(null);
    setEdges((eds) => eds.map((e) => (e.selected ? { ...e, selected: false } : e)));
    setNodes((nds) => nds.map((n) => (n.selected ? { ...n, selected: false } : n)));
  }, [setEdges, setNodes]);

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId) ?? null,
    [nodes, selectedNodeId]
  );
  // All currently-selected user nodes (excluding chrome). Drives the
  // multi-select panel in PropertiesPanel — bulk EXISTING toggle, bulk
  // delete, etc. React Flow updates each node's `selected` flag via
  // box-select / shift-click, so we just filter.
  const selectedNodes = useMemo(
    () => nodes.filter(
      (n) => n.selected && n.id !== '__page_frame__' && n.id !== '__title_block__'
    ),
    [nodes]
  );

  // Single-selected wire (edge), used to route PropertiesPanel to
  // the WireEditor when the user has clicked exactly one wire and
  // no nodes. Multi-wire selection falls through to the existing
  // MultiSelectPanel path for nodes — wire bulk-edit isn't a real
  // use case yet, and treating it as "nothing selected" keeps the
  // panel calm during box-selection sweeps.
  const selectedEdge = useMemo(
    () => {
      if (selectedNodes.length > 0) return null;
      const sel = edges.filter((e) => e.selected);
      return sel.length === 1 ? sel[0] : null;
    },
    [edges, selectedNodes]
  );

  return (
    <div className="canvas-wrapper">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onNodeDragStart={onNodeDragStart}
        onBeforeDelete={onBeforeDelete}
        isValidConnection={isValidConnection}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onNodeClick={onNodeClick}
        onPaneClick={onPaneClick}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        snapToGrid
        snapGrid={[5, 5]}
        connectionRadius={25}
        minZoom={0.2}
        maxZoom={4}
        deleteKeyCode={['Delete', 'Backspace']}
        // Hold Cmd (Mac) or Ctrl (Win/Linux) and drag on the canvas to
        // box-select multiple symbols + wires. Same convention as the
        // main Solar Proof design tool — keeps muscle memory consistent.
        // (Without a modifier, drag still does the default React Flow
        // behaviour: pan if started on the pane, drag-symbol if started
        // on a node.)
        selectionKeyCode={['Meta', 'Control']}
        multiSelectionKeyCode={['Meta', 'Control', 'Shift']}
        // Partial = anything the box TOUCHES is selected. Default
        // ('full') requires the box to fully contain a node, which is
        // surprising for a schematic editor where wires sweep through
        // the canvas — users expect "lasso anything I drag over".
        selectionMode={SelectionMode.Partial}
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} color="#e2e8f0" />
        <Controls position="bottom-right" />
        <MiniMap
          nodeStrokeColor="#0f172a"
          nodeColor="#94a3b8"
          style={{ background: '#f8fafc' }}
        />
      </ReactFlow>

      <PropertiesPanel
        selectedNode={selectedNode ?? null}
        selectedNodes={selectedNodes}
        selectedEdge={selectedEdge}
      />
    </div>
  );
}

function Toolbar() {
  // Auto-save loaded SLD revisions back to the SP backend when the
  // editor was opened from a project context (?project=<qid>).
  // No-op in standalone mode. Hook lives at the toolbar level so
  // it mounts/unmounts with the chrome — Canvas re-renders shouldn't
  // restart the save subscription.
  useAutoSaveRevision();

  const loadTopology = useEditorStore((s) => s.loadTopology);
  const topology = useEditorStore((s) => s.topology);
  const nodes = useEditorStore((s) => s.nodes);
  const edges = useEditorStore((s) => s.edges);
  const projectId = useEditorStore((s) => s.projectId);
  const paperSize = useEditorStore((s) => s.paperSize);
  const setPaperSize = useEditorStore((s) => s.setPaperSize);
  // PDF export needs to manipulate the React Flow viewport (reset to
  // identity for a paper-size capture, then restore). Pulled inside
  // the Toolbar via the same provider that wraps Canvas — ReactFlow-
  // Provider sits at the App root.
  const { getViewport, setViewport, fitView } = useReactFlow();
  const [exporting, setExporting] = useState(false);
  const askConfirm = useEditorStore((s) => s.askConfirm);
  const [cmdkOpen, setCmdkOpen] = useState(false);
  const [newConfigOpen, setNewConfigOpen] = useState(false);
  // Solar Proof Pro Tier 1 upsell — fires after a successful PDF
  // export so the moment of "I just produced something I'm proud of"
  // is the moment we surface what else SP can do. Per-browser
  // suppression flag inside the modal keeps it from badgering users
  // who've already declined.
  const [proModalOpen, setProModalOpen] = useState(false);
  // Subscribe to history.past/future LENGTHS (not the arrays themselves)
  // so the toolbar only re-renders when undo/redo availability changes,
  // not on every push.
  const canUndo = useEditorStore((s) => s.history.past.length  > 0);
  const canRedo = useEditorStore((s) => s.history.future.length > 0);
  const undo = useEditorStore((s) => s.undo);
  const redo = useEditorStore((s) => s.redo);

  const exportJson = () => {
    const out = { topology, layout: { nodes: nodes.filter(n => n.id !== '__page_frame__'), edges } };
    const blob = new Blob([JSON.stringify(out, null, 2)], { type: 'application/json' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = 'sld-export.json';
    a.click();
  };

  /**
   * Export the diagram as a PDF via the backend Gotenberg endpoint.
   * The capture briefly resets the React Flow viewport to identity so
   * the snapshot is exactly paper-sized regardless of the user's
   * current zoom; restored immediately after. Errors surface in a
   * styled confirm dialog so the message lives next to the action.
   */
  const exportPdfHandler = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      const result = await exportPdf({
        topology,
        paperSize,
        getViewport,
        setViewport,
        nodes,
      });
      // The PDF downloaded, but one or more logos couldn't be embedded
      // and went out as blank space. Not worth interrupting the user
      // over — the drawing itself is correct — but we do want to hear
      // about it, because the alternative is finding out when a client
      // screenshots a sheet with a hole where their branding should be.
      if (result.missingImages.length > 0) {
        reportSldClientError({
          context: 'pdf_export_missing_image',
          message:
            `PDF exported with ${result.missingImages.length} unembeddable image(s): ` +
            result.missingImages.join(', '),
          projectId,
        });
      }
      // Successful publish — promote Pro Tier 1, but ONLY in
      // standalone mode. When the editor was opened from a real
      // project (?project=<qid>, so projectId is set) the user is
      // already a Solar Proof customer on Tier 1+ — upselling them
      // the plan they're already on is wrong. Also respects the
      // per-browser permanent dismiss. Wrapped in a timeout so the
      // browser's download UI lands first; the modal popping
      // simultaneously with the file dialog feels jumpy.
      if (projectId === null && !isProUpgradeSuppressed()) {
        setTimeout(() => setProModalOpen(true), 600);
      }
    } catch (err) {
      // Belt-and-braces: exportPdf normalises capture failures into
      // real Errors, but never let a raw thrown object stringify to
      // "[object Event]" / "[object Object]" in the dialog again.
      const msg =
        err instanceof Error
          ? err.message
          : typeof Event !== 'undefined' && err instanceof Event
            ? 'Unexpected browser event during export (see console for details)'
            : String(err);
      // Full detail always goes to the console (support can ask for it),
      // and to the server-side audit stream via the beacon — see
      // api/reportClientError.ts for why this catches failures that
      // never reach sld-api/pdf.php (404 / WAF 403 / network).
      console.error('SLD PDF export failed:', err);
      reportSldClientError({ context: 'pdf_export', message: msg, projectId });
      // Keep a short technical reference in the dialog so a client
      // screenshot is actionable, but lead with plain language. The
      // old copy ("Is the Solar Proof backend (XAMPP) running…") was
      // dev-machine wording and confused clients on production — it's
      // now shown in dev builds only.
      const detail = msg.length > 160 ? msg.slice(0, 160) + '…' : msg;
      void askConfirm({
        title: 'PDF export failed',
        message: import.meta.env.DEV
          ? `${msg}\n\nIs the Solar Proof backend (XAMPP) running, and is Gotenberg reachable at http://localhost:3000? ` +
            `Check the backend logs for details.`
          : `Sorry — the PDF couldn't be generated just now. Please try again in a moment.\n\n` +
            `If it keeps happening, contact Solar Proof support and mention this detail ` +
            `(it has also been logged on our end):\n\n${detail}`,
        singleButton: true,
        tone: 'warning',
      });
    } finally {
      setExporting(false);
    }
  };

  // Preset switch — extracted because it's now triggered from BOTH
  // the inline select and the command palette. Made async so we can
  // gate on the styled confirm dialog instead of the native one.
  const switchPreset = async (key: string) => {
    const preset = samplePresets[key];
    if (!preset) return;
    const currentIsPreset = Object.values(samplePresets)
      .some(p => p.topology === topology);
    if (!currentIsPreset) {
      const ok = await askConfirm({
        title: 'Switch preset?',
        message:
          'Your current diagram, including any manual position adjustments and label edits, will be replaced. ' +
          'Use Cmd/Ctrl+Z afterwards to restore.',
        confirmLabel: 'Switch preset',
        tone: 'warning',
      });
      if (!ok) return;
    }
    loadTopology(preset.topology);
  };

  // Cmd/Ctrl+K opens the command palette. Don't fire if the user is
  // typing in an input — they might be editing a label and pressing
  // Cmd+K means something else in their context.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (!meta || e.key.toLowerCase() !== 'k') return;
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      setCmdkOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Build the command list. Re-derived per render but cheap; the
  // palette only re-runs its filter when its query/active index
  // changes.
  const commands: Command[] = [
    {
      id: 'new-from-config',
      title: 'New diagram from your install',
      description: 'Pick inverters, panels, strings — auto-draft',
      section: 'File',
      keywords: ['wizard', 'configurator', 'builder', 'create', 'start'],
      run: () => setNewConfigOpen(true),
    },
    {
      id: 'export-pdf',
      title: 'Export PDF',
      description: 'Print-ready, via Gotenberg',
      section: 'Export',
      shortcut: '⌘E',
      keywords: ['save', 'download', 'print'],
      run: () => { void exportPdfHandler(); },
    },
    {
      id: 'export-json',
      title: 'Export JSON',
      description: 'Raw topology + layout',
      section: 'Export',
      keywords: ['save', 'download'],
      run: exportJson,
    },
    {
      id: 'undo',
      title: 'Undo',
      section: 'Edit',
      shortcut: '⌘Z',
      run: undo,
    },
    {
      id: 'redo',
      title: 'Redo',
      section: 'Edit',
      shortcut: '⌘⇧Z',
      run: redo,
    },
    {
      id: 'fit-view',
      title: 'Fit drawing to window',
      section: 'View',
      shortcut: '⌘1',
      keywords: ['zoom', 'reset'],
      run: () => fitView({ padding: 0.1, duration: 200 }),
    },
    {
      id: 'paper-a4-l',
      title: 'A4 Landscape',
      section: 'Paper size',
      keywords: ['paper', 'a4'],
      run: () => setPaperSize('A4-landscape'),
    },
    {
      id: 'paper-a3-l',
      title: 'A3 Landscape',
      section: 'Paper size',
      keywords: ['paper', 'a3'],
      run: () => setPaperSize('A3-landscape'),
    },
    {
      id: 'paper-a3-p',
      title: 'A3 Portrait',
      section: 'Paper size',
      keywords: ['paper', 'a3', 'portrait'],
      run: () => setPaperSize('A3-portrait'),
    },
    // Each preset becomes its own command so users can jump to a
    // sample without hunting through the dropdown.
    ...Object.entries(samplePresets).map(([key, p]) => ({
      id: `preset-${key}`,
      title: `Load: ${p.title}`,
      description: p.desc,
      section: 'Preset',
      keywords: ['preset', 'sample', 'load', p.title.toLowerCase()],
      run: () => { void switchPreset(key); },
    })),
  ];

  return (
    <div className="toolbar">
      {/* GROUP: brand */}
      <div className="toolbar-brand">
        <img
          src="./sp-logo.png"
          alt="Solar Proof"
          title="Solar Proof · SLD Editor"
          onError={(e) => {
            // Fallback to a minimal wordmark if the logo image fails to load.
            const span = document.createElement('span');
            span.textContent = 'Solar Proof';
            span.style.cssText = 'font-weight:700;font-size:14px;';
            e.currentTarget.replaceWith(span);
          }}
        />
      </div>

      <div className="toolbar-divider" aria-hidden />

      {/* GROUP: file (new / paper)
          Split-button pattern: primary "+ New" goes straight to the
          configurator wizard (the path 99% of users want), and the
          chevron beside it exposes the pre-built sample presets for
          experimentation / demo purposes.

          Hidden in project mode — when the editor is opened with
          ?project=<qid> from SP's publish menu, the user is editing
          THEIR project's diagram, not exploring. The wizard + sample
          presets are standalone-mode entry points and would just
          confuse the workflow there. The "↻ From project" action
          inside the Revisions menu replaces the regenerate path
          for project-mode users. */}
      {!projectId && (
        <div
          className="toolbar-group"
          style={{ gap: 0 }}
        >
          <button
            className="btn-primary"
            onClick={() => setNewConfigOpen(true)}
            title="Start a new diagram from your install"
            style={{
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              paddingRight: 'var(--space-3)',
            }}
          >
            + New
          </button>
          <Menu
            triggerClassName="btn-primary"
            triggerTitle="Load a pre-built sample diagram"
            align="left"
            headerLabel="Sample diagrams"
            triggerStyle={{
              borderTopLeftRadius: 0,
              borderBottomLeftRadius: 0,
              borderLeft: '1px solid rgba(255, 255, 255, 0.25)',
              paddingLeft: 'var(--space-2)',
              paddingRight: 'var(--space-2)',
              minWidth: 0,
            }}
            trigger={<>▾</>}
            items={Object.entries(samplePresets).map(([key, p]) => ({
              label: p.title,
              description: p.desc,
              onSelect: () => { void switchPreset(key); },
            }))}
          />

          {/* Paper-size selector intentionally omitted — A4 landscape
              is right for ~95% of jobs. A3 sizes are still reachable
              via the Cmd/Ctrl+K command palette ("A3 Landscape" /
              "A3 Portrait") for big commercial drawings. */}
        </div>
      )}

      <div className="toolbar-spacer" />

      {/* GROUP: history */}
      <div className="toolbar-group">
        <button
          className="btn-tertiary"
          onClick={undo}
          disabled={!canUndo}
          title="Undo (Cmd/Ctrl+Z)"
          aria-label="Undo"
        >
          ↶ Undo
        </button>
        <button
          className="btn-tertiary"
          onClick={redo}
          disabled={!canRedo}
          title="Redo (Cmd/Ctrl+Shift+Z)"
          aria-label="Redo"
        >
          ↷ Redo
        </button>
      </div>

      <div className="toolbar-divider" aria-hidden />

      {/* GROUP: cmd palette */}
      <div className="toolbar-group">
        <button
          className="btn-tertiary"
          onClick={() => setCmdkOpen(true)}
          title="Open command palette (Cmd/Ctrl+K)"
          style={{ gap: 8 }}
        >
          Search <span className="kbd">⌘K</span>
        </button>
      </div>

      <div className="toolbar-divider" aria-hidden />

      {/* GROUP: SP project revisions — only renders when the editor
          is opened with ?project=<qid> in the URL. Standalone
          sessions don't see this control. */}
      <div className="toolbar-group">
        <RevisionsMenu />
      </div>

      {/* GROUP: export menu — collapses three buttons into one. */}
      <Menu
        triggerClassName="btn-primary"
        triggerTitle="Export this drawing"
        triggerStyle={{ minWidth: 96 }}
        headerLabel="Export as"
        trigger={<>{exporting ? 'Exporting…' : 'Export'} ▾</>}
        disabled={exporting}
        items={[
          {
            label: 'PDF',
            description: 'Print-ready, via Gotenberg',
            shortcut: '⌘E',
            onSelect: () => { void exportPdfHandler(); },
          },
          {
            label: 'JSON',
            description: 'Raw topology + layout',
            onSelect: exportJson,
          },
        ]}
      />

      {/* GROUP: standalone-mode footer (saved indicator + signup CTA).
          Wrapped in a group so spacing is consistent with the rest of
          the toolbar. */}
      <div className="toolbar-group" style={{ marginLeft: 'var(--space-2)' }}>
        <span
          className="toolbar-pill"
          title="Your work auto-saves to this browser. No account needed."
        >
          Saved locally
        </span>
        {/* The /signup path on solarproof.com.au doesn't exist as a
            standalone page — signup goes through pricing_tiers (pick a
            plan, then enter details). Point this toolbar CTA at the
            pricing page so the click actually lands somewhere useful. */}
        <a
          href="https://www.solarproof.com.au/pricing_tiers"
          target="_blank"
          rel="noopener noreferrer"
          className="btn-tertiary"
          style={{ textDecoration: 'none' }}
          title="Get the full Solar Proof project workflow — proposals, signing, customer portals"
        >
          Full workflow →
        </a>
      </div>

      <CommandPalette
        open={cmdkOpen}
        onOpenChange={setCmdkOpen}
        commands={commands}
      />
      <NewDiagramConfig
        open={newConfigOpen}
        onOpenChange={setNewConfigOpen}
      />
      <ProUpgradeModal
        open={proModalOpen}
        onClose={() => setProModalOpen(false)}
      />
    </div>
  );
}

export default function App() {
  return (
    <ReactFlowProvider>
      <div className="app">
        <Toolbar />
        <div className="app-body">
          <SymbolPalette />
          <Canvas />
        </div>
      </div>
    </ReactFlowProvider>
  );
}
